// react
import React from 'react';
import styled from 'styled-components';
import { css } from 'styled-components';

// spring-carousel
import { useSpringCarousel } from 'react-spring-carousel';

// components
import { Icon, LoadingTransition, Skeleton } from '@/components/atoms';

/**
 * Carousel displays a series of images or videos.
 */
export const Carousel = ({ items, title, isLoading }: CarouselProps) => {
  const { carouselFragment, slideToPrevItem, slideToNextItem } =
    // @ts-ignore
    useSpringCarousel({
      slideType: 'fluid',
      slideAmount: 285,
      items:
        items?.map((item, i) => {
          return {
            ...item,
            index: i,
            renderItem: (
              <StyledCarouselItem>{item.renderItem}</StyledCarouselItem>
            ),
          };
        }) || [],
    });

  if (!items || items.length === 0) return null;
  return (
    <StyledCarousel $isLoading={isLoading}>
      <CarouselHeader>
        <Left>
          <LoadingTransition
            isLoading={isLoading}
            loading={
              <Skeleton
                shape='title'
                width='400px'
                height='36px'
                isMargin={false}
              />
            }
            content={<Title>{title}</Title>}
          />
        </Left>
        <Right>
          <StyledButton onClick={slideToPrevItem}>
            <Icon icon='chevron-left' fontSize={16} />
          </StyledButton>
          <StyledButton onClick={slideToNextItem}>
            <Icon icon='chevron-right' fontSize={16} />
          </StyledButton>
        </Right>
      </CarouselHeader>
      <StyledCarouselContainer>{carouselFragment}</StyledCarouselContainer>
    </StyledCarousel>
  );
};

interface StyledCarouselProps {
  $isLoading?: boolean;
}
const StyledCarousel = styled.div<StyledCarouselProps>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  align-items: stretch;
  gap: ${(props) => props.theme.spacing[12]};

  & *::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }

  ${(props) =>
    props.$isLoading &&
    css`
      pointer-events: none;
    `}
`;

const CarouselHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.div`
  font: ${(props) => props.theme.typography.body.xl.medium};
`;

const Left = styled.div``;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[8]};
`;

const StyledCarouselContainer = styled.div`
  display: flex;
  align-items: center;
  margin: -${(props) => props.theme.spacing[8]};
`;

const StyledCarouselItem = styled.div`
  padding: ${(props) => props.theme.spacing[8]};
  padding-top: ${(props) => props.theme.spacing[12]};
  padding-bottom: ${(props) => props.theme.spacing[12]};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledButton = styled.button`
  all: unset;
  color: ${(props) => props.theme.color.neutral[80]};
  height: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  max-width: 32px;
  height: 32px;
  max-height: 32px;
  border-radius: 99999px;
  border: 1px solid ${(props) => props.theme.color.neutral[80]};

  transition-duration: 0.2s;
  &:hover {
    transform: scale(1.1);
    color: ${(props) => props.theme.color.neutral[90]};
  }
`;

interface CarouselItem {
  id: string;
  renderItem: React.ReactNode;
}

interface CarouselProps {
  items: CarouselItem[];
  title?: string;
  isLoading?: boolean;
}

Carousel.displayName = 'Carousel';

export default Carousel;
